import { DemoConfig } from '@/demo/demoConfig';
import { ExchangeRates } from '@/shared/data/useExchangeRates';
import { SEAAccountFeatureConfigsResponse } from '@/shared/data/useFetchAccountFeatureConfigs';

const DEMO_ACCOUNT_UUID = '00000000-0000-0000-0000-000000000001';

/**
 * Demo-owned defaults for slices the config app does not yet supply. Supplier identity and market
 * data come from the demo config via demoTransform; these constants cover exchange rates and feature
 * configs until the config schema grows to include them.
 */
export const DEMO_EXCHANGE_RATES: ExchangeRates = {
  CAD: { CAD: 1, EUR: 0.678798, MXN: 12.463933, USD: 0.742302 },
  EUR: { EUR: 1, CAD: 1.473193, MXN: 18.361777, USD: 1.093554 },
  MXN: { MXN: 1, CAD: 0.080231, EUR: 0.054461, USD: 0.059556 },
  USD: { USD: 1, CAD: 1.347161, EUR: 0.91445, MXN: 16.790919 },
};

export const DEMO_FEATURE_CONFIGS: SEAAccountFeatureConfigsResponse = {
  data: {
    accountId: DEMO_ACCOUNT_UUID,
    wasSmb: false,
    migratedFromSmbDate: null,
    isManaged: true,
    featureConfigs: [],
  },
};

/** Resolved when ?demo is present without a config UUID; loads {@link DEFAULT_DEMO_CONFIG}. */
export const DEFAULT_DEMO_ID = 'default';

/**
 * (GET /api/admin/demo-configs/:uuid) does not exist yet (SUPP-18862) and fetchDemoConfig returns null.
 *
 * Sourced verbatim from the entity JSON produced by the config app (ARROW LLP supplier / Home Depot
 * buyer). Kept as typed TS literals rather than a JSON import so it stays type-checked against DemoConfig.
 */
export const DEFAULT_DEMO_CONFIG: DemoConfig = {
  organizations: [
    {
      id: 5001,
      uuid: 'arrow-llp-org-uuid',
      name: 'ARROW LLP',
      type: 'supplier',
      country: 'US',
      industry: 'Manufacturing',
    },
    {
      id: 5002,
      uuid: 'home-depot-org-uuid',
      name: 'Home Depot',
      type: 'buyer',
      currency: 'USD',
      country: 'US',
      industry: 'Retail',
    },
  ],
  divisions: [
    {
      id: 6001,
      uuid: 'home-depot-division-uuid',
      name: 'Home Depot',
      organizationId: 5002,
      organizationUuid: 'home-depot-org-uuid',
      type: 'buyer',
      address: {
        addressLine: '123 Main St',
        city: 'New York',
        state: 'NY',
        country: 'US',
        postalCode: '10001',
      },
      employeeCount: 1000,
      naics: '452000',
    },
    {
      id: 6002,
      uuid: 'supplier-division-uuid-0',
      name: 'Acme Corp',
      organizationId: 5001,
      organizationUuid: 'arrow-llp-org-uuid',
      type: 'supplier',
      address: {
        addressLine: '456 Industrial Blvd',
        city: 'New York',
        state: 'NY',
        country: 'US',
        postalCode: '10001',
      },
      employeeCount: 500,
      naics: '336000',
    },
  ],
  markets: [
    {
      id: 7001,
      uuid: 'market-uuid-0',
      name: 'Home Depot Daily',
      makerId: 6001,
      makerUuid: 'home-depot-division-uuid',
      makerOrganizationUuid: 'home-depot-org-uuid',
      type: 'price-discovery',
      currency: 'USD',
      isEnabled: true,
      marketType: 'DOMESTIC',
      nextClearDate: '2026-06-05T16:00:00.000Z',
      payDate: '2026-06-08T20:19:37.058Z',
      cashpoolSize: 500000,
      maxApr: 8,
      maxDiscount: null,
      trendingRate: 7,
      expressAcceptRate: 8,
      rateType: 'APR',
      productType: 'STANDARD',
    },
  ],
  takerMarkets: [
    {
      id: 8001,
      marketUuid: 'market-uuid-0',
      marketId: 7001,
      takerUuid: 'supplier-division-uuid-0',
      takerId: 6002,
      makerUuid: 'home-depot-division-uuid',
      makerId: 6001,
      makerOrganizationUuid: 'home-depot-org-uuid',
      makerOrganizationId: 5002,
      makerDivisionName: 'Home Depot Division',
      makerOrganizationName: 'Home Depot',
      takerOrganizationId: 5001,
      takerDivisionName: 'Acme Corp',
      takerOrganizationName: 'ARROW LLP',
      currency: 'USD',
      takerMakerId: '6002-6001',
      eligibleInvoiceCount: 20,
      eligibleInvoiceAmount: 1000,
      eligibleDpeWeightedAverage: 30,
      eligibleDpeWeightedSum: 30000,
      acceptedInvoiceCount: 0,
      acceptedInvoiceAmount: 0,
      awardedInvoiceCount: 0,
      awardedInvoiceAmount: 0,
      takerExcludedInvoiceCount: 0,
      takerExcludedInvoiceAmount: 0,
    },
  ],
  supplier: {
    id: 9001,
    uuid: 'user-uuid-001',
    email: 'jane.williams@arrowllp.com',
    firstName: 'Jane',
    lastName: 'Williams',
    role: 'Finance Manager',
    organizationId: 5001,
    organizationUuid: 'arrow-llp-org-uuid',
    divisionIds: [6002],
    divisionUuids: ['supplier-division-uuid-0'],
    type: 'supplier',
    currency: 'USD',
    language: 'en',
    region: 'USA',
    srm: {
      firstName: 'Jon',
      lastName: 'Baldwin',
      email: 'jon.baldwin@c2fo.com',
      phone: '+1 (913) 850-7029',
      imageUrl:
        'https://c2fo-static.s3-us-west-2.amazonaws.com/app/srm-images/srm-transparent/jon.baldwin@c2fo.com.png',
    },
  },
  config: {
    showPricing: true,
    showNpsSurvey: false,
    enableSimulations: true,
    generatedAt: '2026-06-04T20:19:37.059Z',
    version: '2.0',
    marketingWidget: 'how-to',
    products: {
      financeIQ: true,
      lendingConnections: true,
    },
  },
  registrationConfig: {
    buyerPermalink: 'homedepot',
    buyerMarketingConfig: {
      domain: 'homedepot',
      buyerUuid: 'buyer-uuid-homedepot',
      buyerDivisionUuid: 'division-uuid-homedepot',
      makerOrganizationUuid: 'home-depot-org-uuid',
      buyerName: 'Home Depot',
      imagePng: null,
      imageSvg: 'https://static.c2fo.com/c2fo/assets/img/buyers/homedepot.212caAasd.svg',
      lockupImage: null,
      logoDisplayMode: 'lockup',
      defaultLocale: 'en-US',
      supportPhone: null,
      supportEmail: null,
      localeOverrides: null,
      executiveContactName: null,
      executiveContactTitle: null,
      isActive: true,
    },
    accountLinkingOutcome: 'account_linked',
  },
};
